import { useController } from "react-hook-form";
import { useEffect, useState } from "react";
import { Info } from "lucide-react";

import { TextInputFieldProps } from "./types";
import { inputStyles } from "./inputStyles";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

export const TextInputField = ({ field, form, stepId, size = "desktop" }: TextInputFieldProps) => {
  const { trigger, watch } = form;
  const [isInitiated, setIsInitiated] = useState(false);
  const fieldsInputs = watch(stepId);
  const isDisplayed = field.showIf ? field.showIf?.(fieldsInputs) : true;
  const key = `${stepId}.${field.id}`;
  const {
    field: { onChange, value },
    fieldState: { error, isDirty },
  } = useController({
    rules: {
      required: isDisplayed ? field.required : false,
      validate: val => (isDisplayed ? field.validation?.(val, fieldsInputs) : undefined),
    },
    control: form.control,
    name: key,
  });

  useEffect(() => {
    if (!isDisplayed && error) {
      form.clearErrors(key);
      setIsInitiated(false);
    }
  }, [form, key, isDisplayed, error]);

  useEffect(() => {
    if (!isDisplayed && value) {
      onChange("");
      setIsInitiated(false);
    }
  }, [isDisplayed, value, onChange]);

  useEffect(() => {
    if (isDisplayed) {
      setIsInitiated(true);
      trigger(key);
    }
  }, [isDisplayed, key, trigger, isInitiated]);

  return (
    <div
      className={cn(inputStyles.container({ size }), {
        hidden: !isDisplayed,
      })}
      key={field.id}
    >
      <div className="flex items-center gap-2">
        <label className={inputStyles.label({ size })}>
          {typeof field.label === "function" ? field.label(fieldsInputs) : field.label}
        </label>
        {field.tooltip && (
          <BasicTooltip content={field.tooltip}>
            <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
          </BasicTooltip>
        )}
      </div>
      <Input
        onChange={e => {
          onChange(e.target.value);
        }}
        readOnly={field.readOnly}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        type={field.inputType || "text"}
        value={value}
        className={inputStyles.input({ size })}
        placeholder={
          typeof field.placeholder === "function"
            ? field.placeholder(fieldsInputs)
            : field.placeholder
        }
      />
      {error && isDirty && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
};
